import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Card, Button, Table, Tooltip, Modal } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { EMPLOYEE_ROLES } from "src/appV2/Employee/constants";
import { SessionType } from "src/modules/interface";
import { showErrorToast, showSuccessToast } from "src/appV2/lib/Notifications";
import {
  ShoppableQualificationIncludedQualification,
  useGetWorkerShoppableQualifications,
} from "@src/appV2/WorkerShoppableQualifications/api/useGetWorkerShoppableQualifications";
import { useDeleteWorkerShoppableQualification } from "@src/appV2/WorkerShoppableQualifications/api/useDeleteWorkerShoppableQualification";
import { API_TYPES, WorkerShoppableQualification } from "@clipboard-health/contract-backend-main";
import { ColumnsType } from "antd/lib/table";
import { AddWorkerRoleModal } from "./AddWorkerRoleModal";

interface WorkerRolesManagerParams {
  workerId: string;
}

export function WorkerRolesManager(params: WorkerRolesManagerParams) {
  const { workerId } = params;

  const employeeRoles = useSelector<SessionType, string[]>(
    (state) => state.session.adminProfile.roles || []
  );
  const canManageRoles = employeeRoles.includes(EMPLOYEE_ROLES.WORKER_LICENSE_MANAGER);

  const { data: workerRoles, isLoading: areRolesLoading } = useGetWorkerShoppableQualifications({
    workerId,
    queryParams: {
      include: ["qualifications"],
    },
  });
  const workerRolesIncludedQualificationsIdMap =
    workerRoles?.included
      ?.filter((data) => data?.type === API_TYPES.qualifications)
      .reduce(
        (acc, qualification) => {
          acc[qualification.id] = qualification;
          return acc;
        },
        {} as Record<string, ShoppableQualificationIncludedQualification>
      ) ?? {};

  const { mutateAsync: deleteRole, isLoading: isDeletingRole } =
    useDeleteWorkerShoppableQualification(workerId);

  const handleDeleteRole = async (role: WorkerShoppableQualification) => {
    const roleName =
      workerRolesIncludedQualificationsIdMap[role.relationships.qualification.data.id].attributes
        .displayLabel;
    Modal.confirm({
      title: "Delete Role",
      content: `Are you sure you want to remove the ${roleName} role? This will not delete associated licenses.`,
      onOk: async () => {
        try {
          await deleteRole({ shoppableQualificationId: role.id });
          showSuccessToast("Role removed successfully");
          // FIXME: Automatically disabled during ESLint config update
        } catch {
          showErrorToast("Failed to remove role");
        }
      },
    });
  };
  const columns: ColumnsType<WorkerShoppableQualification> = [
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (_, role) => {
        return workerRolesIncludedQualificationsIdMap[role.relationships.qualification.data.id]
          ?.attributes.displayLabel;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, role) =>
        canManageRoles && (
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteRole(role)}
          >
            Remove
          </Button>
        ),
    },
  ];

  const [showAddQualificationModal, setShowAddQualificationModal] = useState(false);

  return (
    <Card
      title="Selected Roles"
      extra={
        canManageRoles && (
          <Tooltip placement="top" title="Add a Role">
            <Button
              data-testid="add-role-button"
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              onClick={() => setShowAddQualificationModal(true)}
            />
          </Tooltip>
        )
      }
    >
      <Table
        columns={columns}
        dataSource={workerRoles?.data}
        loading={areRolesLoading || isDeletingRole}
        size="small"
        locale={{ emptyText: "No roles selected" }}
      />
      <AddWorkerRoleModal
        workerId={workerId}
        opened={showAddQualificationModal}
        closeModal={() => setShowAddQualificationModal(false)}
      />
    </Card>
  );
}
